import {
  normalizeSkillKey,
  normalizeCommaSeparatedTextQuery,
  parseCommaList,
  parseTextFilterOperator,
} from '@yeshuman/sdk'
import type { TextFilterOperator, SkillMatchMode } from '@yeshuman/sdk'
import type { OpportunitiesFilters, Opportunity } from '../hooks/use-opportunities'
import type { JobPostedSince, JobSourceKind, CandidateJobsSort } from './list-filters/candidate-jobs-constants'
import {
  EMPLOYMENT_TYPE_SET,
  SOURCE_KIND_SET,
  WORK_MODE_SET,
} from './list-filters/candidate-jobs-constants'

/** Candidate job browse always matches any selected skill (OR); never AND / API skills param. */
export const JOB_BROWSE_SKILL_MATCH_MODE = 'or' as const satisfies SkillMatchMode

export type JobBrowseFilters = {
  q: string
  organisation: string
  organisation_operator: TextFilterOperator
  location: string
  location_operator: TextFilterOperator
  selectedSkills: string[]
  skillMatchMode: SkillMatchMode
  work_mode: string[]
  employment_type: string[]
  published_since: JobPostedSince | ''
  source: JobSourceKind[]
  sort: CandidateJobsSort
  page: number
  page_size: number
}

/** Partial browse state for smart-analysis bridge and URL preference chips. */
export type JobBrowseFilterInput = Partial<
  Pick<
    JobBrowseFilters,
    | 'q'
    | 'organisation'
    | 'organisation_operator'
    | 'location'
    | 'location_operator'
    | 'selectedSkills'
    | 'skillMatchMode'
    | 'work_mode'
    | 'employment_type'
    | 'published_since'
    | 'source'
  >
>

export type JobBrowseFacet = {
  key: string
  label: string
  count: number
}

export const DEFAULT_JOB_BROWSE_FILTERS: JobBrowseFilters = {
  q: '',
  organisation: '',
  organisation_operator: 'contains',
  location: '',
  location_operator: 'contains',
  selectedSkills: [],
  skillMatchMode: JOB_BROWSE_SKILL_MATCH_MODE,
  work_mode: [],
  employment_type: [],
  published_since: '',
  source: [],
  sort: 'updated_desc',
  page: 1,
  page_size: 20,
}

const TEXT_OPERATOR_LABELS: Record<TextFilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  contains: 'contains',
  does_not_contain: 'does not contain',
}

function parsePostedSince(value: string | null): JobPostedSince | '' {
  if (value === '24h' || value === '7d' || value === '30d') return value
  return ''
}

export function parseCandidateJobsSort(value: string | null): CandidateJobsSort {
  if (value === 'updated_asc' || value === 'title_asc' || value === 'title_desc') return value
  return 'updated_desc'
}

export function parseJobBrowseFiltersFromSearchParams(
  searchParams: URLSearchParams,
): JobBrowseFilters {
  return {
    q: normalizeCommaSeparatedTextQuery(searchParams.get('q') ?? ''),
    organisation: normalizeCommaSeparatedTextQuery(searchParams.get('organisation') ?? ''),
    organisation_operator: parseTextFilterOperator(searchParams.get('organisation_op')),
    location: normalizeCommaSeparatedTextQuery(searchParams.get('location') ?? ''),
    location_operator: parseTextFilterOperator(searchParams.get('location_op')),
    selectedSkills: parseCommaList(searchParams.get('skills')),
    skillMatchMode: JOB_BROWSE_SKILL_MATCH_MODE,
    work_mode: parseCommaList(searchParams.get('work_mode'), WORK_MODE_SET),
    employment_type: parseCommaList(searchParams.get('employment'), EMPLOYMENT_TYPE_SET),
    published_since: parsePostedSince(searchParams.get('published_since')),
    source: parseCommaList(searchParams.get('source'), SOURCE_KIND_SET) as JobSourceKind[],
    sort: parseCandidateJobsSort(searchParams.get('sort')),
    page: parseInt(searchParams.get('page') || '1', 10) || 1,
    page_size: parseInt(searchParams.get('page_size') || '20', 10) || 20,
  }
}

const FILTER_QUERY_KEYS = [
  'q',
  'organisation',
  'organisation_op',
  'location',
  'location_op',
  'listing_type',
  'skills',
  'skill_mode', // legacy; stripped on write — browse is OR-only
  'work_mode',
  'employment',
  'published_since',
  'source',
  'sort',
  'page',
  'page_size',
] as const

export function applyJobBrowseFiltersToSearchParams(
  base: URLSearchParams,
  filters: JobBrowseFilters,
): URLSearchParams {
  const next = new URLSearchParams(base)

  for (const key of FILTER_QUERY_KEYS) {
    next.delete(key)
  }

  if (filters.q.trim()) next.set('q', normalizeCommaSeparatedTextQuery(filters.q))
  if (filters.organisation.trim()) {
    const organisation = normalizeCommaSeparatedTextQuery(filters.organisation)
    next.set('organisation', organisation)
    if (filters.organisation_operator !== 'contains') {
      next.set('organisation_op', filters.organisation_operator)
    }
  }
  if (filters.location.trim()) {
    const location = normalizeCommaSeparatedTextQuery(filters.location)
    next.set('location', location)
    if (filters.location_operator !== 'contains') {
      next.set('location_op', filters.location_operator)
    }
  }
  if (filters.selectedSkills.length > 0) {
    next.set('skills', filters.selectedSkills.join(','))
  }
  if (filters.work_mode.length > 0) next.set('work_mode', filters.work_mode.join(','))
  if (filters.employment_type.length > 0) next.set('employment', filters.employment_type.join(','))
  if (filters.published_since) next.set('published_since', filters.published_since)
  if (filters.source.length > 0) next.set('source', filters.source.join(','))
  if (filters.sort !== 'updated_desc') next.set('sort', filters.sort)
  if (filters.page > 1) next.set('page', filters.page.toString())
  if (filters.page_size !== 20) next.set('page_size', filters.page_size.toString())

  return next
}

export function jobBrowseFiltersToOpportunitiesFilters(
  filters: JobBrowseFilters,
): OpportunitiesFilters {
  const q = filters.q.trim() || undefined
  return {
    q,
    // Browse bar expects substring match in title/description — not API default hybrid
    // semantic search (which can omit jobs that literally contain the query).
    ...(q ? { mode: 'keyword' as const } : {}),
    organisation: filters.organisation.trim() || undefined,
    organisation_operator: filters.organisation_operator,
    location: filters.location.trim() || undefined,
    location_operator: filters.location_operator,
    // Skills are OR-matched client-side; omit from API so managed-only skills gate is not applied.
    skills: undefined,
    work_mode: filters.work_mode.length > 0 ? filters.work_mode : undefined,
    employment_type: filters.employment_type.length > 0 ? filters.employment_type : undefined,
    published_since: filters.published_since || undefined,
    source: filters.source.length > 0 ? filters.source : undefined,
    sort: filters.sort,
    page: filters.page,
    page_size: filters.page_size,
  }
}

/** Page size for the secondary harvest used to populate skill filter facets. */
export const SKILL_FACET_HARVEST_PAGE_SIZE = 100

/**
 * Skill picker catalog is independent of active browse filters — harvest the full tenant cohort.
 */
export function jobBrowseFiltersForSkillFacetHarvest(): ReturnType<
  typeof jobBrowseFiltersToOpportunitiesFilters
> {
  return jobBrowseFiltersToOpportunitiesFilters({
    ...DEFAULT_JOB_BROWSE_FILTERS,
    page: 1,
    page_size: SKILL_FACET_HARVEST_PAGE_SIZE,
  })
}

export function hasActiveJobBrowseFilters(filters: JobBrowseFilters): boolean {
  return Boolean(
    filters.sort !== 'updated_desc' ||
      filters.q.trim() ||
      filters.organisation.trim() ||
      filters.location.trim() ||
      filters.selectedSkills.length > 0 ||
      filters.work_mode.length > 0 ||
      filters.employment_type.length > 0 ||
      filters.published_since ||
      filters.source.length > 0,
  )
}

export function buildJobSkillFacets(
  opportunities: Opportunity[],
  limit = 24,
): JobBrowseFacet[] {
  const counts = new Map<string, { label: string; count: number }>()

  for (const opportunity of opportunities) {
    for (const skill of opportunity.skills ?? []) {
      const label = skill.skill_name?.trim()
      if (!label) continue
      const key = normalizeSkillKey(label)
      const existing = counts.get(key)
      if (existing) {
        existing.count += 1
      } else {
        counts.set(key, { label, count: 1 })
      }
    }
  }

  return [...counts.values()]
    .filter((facet) => facet.count > 0)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map(({ label, count }) => ({
      key: normalizeSkillKey(label),
      label,
      count,
    }))
}

export function toggleSkillInJobBrowseFilters(
  filters: JobBrowseFilters,
  skillLabel: string,
): JobBrowseFilters {
  const key = normalizeSkillKey(skillLabel)
  const selected = filters.selectedSkills.some((skill) => normalizeSkillKey(skill) === key)
  const selectedSkills = selected
    ? filters.selectedSkills.filter((skill) => normalizeSkillKey(skill) !== key)
    : [...filters.selectedSkills, skillLabel]

  return {
    ...filters,
    selectedSkills,
    page: 1,
  }
}

export function buildJobBrowseActiveFilterChips(
  filters: JobBrowseFilters,
): { key: string; label: string }[] {
  const chips: { key: string; label: string }[] = []
  const q = filters.q.trim()
  const organisation = filters.organisation.trim()
  const location = filters.location.trim()

  if (q) chips.push({ key: 'q', label: `Search: ${q}` })
  if (organisation) {
    chips.push({
      key: 'organisation',
      label: `Organisation ${TEXT_OPERATOR_LABELS[filters.organisation_operator]} ${organisation}`,
    })
  }
  if (location) {
    chips.push({
      key: 'location',
      label: `Location ${TEXT_OPERATOR_LABELS[filters.location_operator]} ${location}`,
    })
  }
  for (const skill of filters.selectedSkills) {
    chips.push({ key: `skill:${normalizeSkillKey(skill)}`, label: `Skill: ${skill}` })
  }

  return chips
}

export function removeJobBrowseFilterChipKey(
  filters: JobBrowseFilters,
  chipKey: string,
): JobBrowseFilters {
  if (chipKey.startsWith('skill:')) {
    const key = chipKey.slice('skill:'.length)
    return {
      ...filters,
      selectedSkills: filters.selectedSkills.filter(
        (skill) => normalizeSkillKey(skill) !== key,
      ),
      page: 1,
    }
  }
  switch (chipKey) {
    case 'q':
      return { ...filters, q: '', page: 1 }
    case 'organisation':
      return { ...filters, organisation: '', organisation_operator: 'contains', page: 1 }
    case 'location':
      return { ...filters, location: '', location_operator: 'contains', page: 1 }
    default:
      return filters
  }
}

function opportunitySkillKeys(opportunity: Opportunity): Set<string> {
  return new Set(
    (opportunity.skills ?? [])
      .map((skill) => normalizeSkillKey(skill.skill_name))
      .filter(Boolean),
  )
}

export function filterOpportunitiesBySelectedSkills(
  opportunities: Opportunity[],
  filters: JobBrowseFilters,
): Opportunity[] {
  if (filters.selectedSkills.length === 0) {
    return opportunities
  }

  const selected = filters.selectedSkills.map(normalizeSkillKey)
  return opportunities.filter((opportunity) => {
    const keys = opportunitySkillKeys(opportunity)
    return selected.some((skill) => keys.has(skill))
  })
}

export function matchedSkillKeysForOpportunity(
  opportunity: Opportunity,
  selectedSkills: string[],
): string[] {
  if (selectedSkills.length === 0) return []
  const keys = opportunitySkillKeys(opportunity)
  return selectedSkills
    .map(normalizeSkillKey)
    .filter((key) => keys.has(key))
}
